import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  CalendarCheck, Loader2, Link2, ExternalLink, Video, AlertTriangle, RefreshCw, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

/** IST date (YYYY-MM-DD) for a day offset from now, read in Asia/Kolkata. */
const istDate = (offsetDays = 0) => {
  const now = new Date(Date.now() + offsetDays * 86400000);
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

/** Render a stored '+05:30' timestamp as Indian time without browser conversion. */
const formatIst = (iso) => {
  if (!iso) return '';
  const [datePart, rest] = iso.split('T');
  if (!rest) return iso;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = rest.slice(0, 5).split(':').map(Number);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const period = hh >= 12 ? 'pm' : 'am';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${d} ${months[m - 1]} ${y}, ${h12}:${String(mm).padStart(2, '0')} ${period} IST`;
};

const formatStamp = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }) + ' IST';
  } catch { return iso; }
};

const Row = ({ label, children, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
    <span className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-medium sm:w-40 shrink-0">{label}</span>
    <span className="text-sm text-[#1F2937] break-all" data-testid={testId}>{children}</span>
  </div>
);

export default function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');
  const [date, setDate] = useState(istDate(1));
  const [time, setTime] = useState('15:00');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/google-calendar/status');
      setStatus(res.data);
    } catch {
      setProblem('Could not load the Google Calendar connection status.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy('connect'); setProblem('');
    try {
      const res = await api.post('/admin/google-calendar/authorize-url');
      window.location.href = res.data.authorization_url;
    } catch (err) {
      setProblem(err.response?.data?.detail || 'Could not start the Google authorization.');
      setBusy('');
    }
  };

  const act = async (key, fn, successMessage) => {
    setBusy(key); setProblem('');
    try {
      const res = await fn();
      setStatus(res.data);
      toast.success(successMessage);
    } catch (err) {
      setProblem(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy('');
  };

  const createTestEvent = () => {
    if (!date || !time) { setProblem('Pick a date and time for the test event.'); return; }
    act('create', () => api.post('/admin/google-calendar/test-event', { start: `${date}T${time}` }),
      'Test event created in Google Calendar');
  };

  const refreshMeet = () => act('refresh',
    () => api.post('/admin/google-calendar/test-event/refresh'), 'Refreshed from Google');

  const deleteTestEvent = () => {
    setConfirmDelete(false);
    act('delete', () => api.delete('/admin/google-calendar/test-event'), 'Test event deleted');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="google-calendar-card">
        <Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" />
      </div>
    );
  }

  const s = status || {};
  const connected = s.connected;
  const event = s.test_event;
  const meetPending = event && !event.meet_link;

  const pill = connected
    ? { text: 'Connected', className: 'bg-green-50 text-green-700' }
    : s.configured
      ? { text: 'Not connected', className: 'bg-gray-100 text-gray-500' }
      : { text: 'Setup needed', className: 'bg-amber-50 text-amber-700' };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 max-w-3xl" data-testid="google-calendar-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F5F6F8] flex items-center justify-center shrink-0">
            <CalendarCheck className="h-5 w-5 text-[#F97D6A]" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#1F2937] font-['Outfit']">Google Calendar Integration</h3>
            <p className="text-sm text-[#6B7280] mt-0.5">
              {connected
                ? 'CareersPOV can create calendar events with Google Meet links on the configured calendar.'
                : 'Authorize the CareersPOV platform account once to create events and Meet links.'}
            </p>
          </div>
        </div>
        <Badge className={`border-0 text-[11px] shrink-0 ${pill.className}`} data-testid="gc-status-pill">
          {pill.text}
        </Badge>
      </div>

      {!s.configured && !connected && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3" data-testid="gc-setup-notice">
          <p className="text-sm text-amber-800">
            Waiting on server configuration: {s.missing?.join(', ')}. Add these to the backend environment, then reload this page.
          </p>
        </div>
      )}

      {problem && (
        <div className="mt-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 flex items-start gap-2" data-testid="gc-problem">
          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{problem}</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100">
        <Row label="Required account" testId="gc-required-account">{s.required_account || '—'}</Row>
        {connected && <Row label="Authorized account" testId="gc-account-email">{s.account_email}</Row>}
        <Row label="Calendar" testId="gc-calendar-name">
          {s.calendar_name || (connected ? '—' : 'Verified after connecting')}
        </Row>
        <Row label="Calendar ID" testId="gc-calendar-id">{s.calendar_id || '—'}</Row>
        {connected && (
          <>
            <Row label="Connected on" testId="gc-connected-at">{formatStamp(s.connected_at)}</Row>
            <Row label="Last Google call" testId="gc-last-success">{formatStamp(s.last_success_at)}</Row>
          </>
        )}
      </div>

      {!connected && (
        <Button
          onClick={connect}
          disabled={!s.configured || busy === 'connect'}
          className="mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl"
          data-testid="gc-connect-btn"
        >
          {busy === 'connect' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
          Connect Google Calendar
        </Button>
      )}

      {connected && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-sm font-semibold text-[#1F2937]">Verification event</p>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Creates one clearly labelled test event with a Meet link. No guests are invited and Google sends no notifications.
          </p>

          {!event ? (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1 min-w-0">
                <Label className="text-xs text-[#6B7280]">Date (IST)</Label>
                <Input
                  type="date" value={date} min={istDate(0)} onChange={(e) => setDate(e.target.value)}
                  className="mt-1 rounded-xl h-10 border-gray-200" data-testid="gc-test-date"
                />
              </div>
              <div className="sm:w-32">
                <Label className="text-xs text-[#6B7280]">Start time (IST)</Label>
                <Input
                  type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="mt-1 rounded-xl h-10 border-gray-200" data-testid="gc-test-time"
                />
              </div>
              <div className="sm:w-28">
                <Label className="text-xs text-[#6B7280]">Duration</Label>
                <p className="mt-1 h-10 flex items-center text-sm text-[#6B7280]" data-testid="gc-test-duration">
                  {s.test_event_duration_minutes} minutes
                </p>
              </div>
              <Button
                onClick={createTestEvent} disabled={busy === 'create'}
                className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10 shrink-0"
                data-testid="gc-create-test-event-btn"
              >
                {busy === 'create' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Create Test Event
              </Button>
            </div>
          ) : (
            <div className="mt-3 rounded-xl bg-[#F8F9FA] border border-gray-100 p-4" data-testid="gc-test-event">
              <p className="text-sm font-medium text-[#1F2937]">{event.summary}</p>
              <p className="text-sm text-[#4B5563] mt-0.5" data-testid="gc-test-event-start">
                {formatIst(event.start_ist)} · {event.duration_minutes} minutes
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-3">
                {event.html_link && (
                  <a
                    href={event.html_link} target="_blank" rel="noreferrer"
                    className="text-sm text-[#F97D6A] hover:underline inline-flex items-center gap-1.5"
                    data-testid="gc-event-link"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Open in Google Calendar
                  </a>
                )}
                {event.meet_link ? (
                  <a
                    href={event.meet_link} target="_blank" rel="noreferrer"
                    className="text-sm text-[#F97D6A] hover:underline inline-flex items-center gap-1.5"
                    data-testid="gc-meet-link"
                  >
                    <Video className="h-3.5 w-3.5" /> Join Google Meet
                  </a>
                ) : (
                  <span className="text-sm text-[#6B7280]" data-testid="gc-meet-pending">
                    Event created — Meet link still pending from Google
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {meetPending && (
                  <Button
                    variant="outline" size="sm" onClick={refreshMeet} disabled={busy === 'refresh'}
                    className="rounded-lg border-gray-200 text-xs" data-testid="gc-refresh-meet-btn"
                  >
                    {busy === 'refresh'
                      ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Check for Meet link
                  </Button>
                )}
                <Button
                  variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} disabled={busy === 'delete'}
                  className="rounded-lg text-xs text-[#6B7280] hover:text-red-600"
                  data-testid="gc-delete-test-event-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Test Event
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {connected && s.sync && (
        <div className="mt-5 pt-5 border-t border-gray-100" data-testid="gc-sync-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#1F2937]">Session synchronisation</p>
              <p className="text-xs text-[#6B7280] mt-0.5" data-testid="gc-sync-counts">
                {s.sync.synced} synced · {s.sync.pending} pending · {s.sync.failed} failed
                {s.sync.sandbox ? ' · sandbox mode' : ''}
              </p>
            </div>
            {(s.sync.pending > 0 || s.sync.failed > 0) && (
              <Button
                variant="outline" size="sm" disabled={busy === 'retry'}
                onClick={() => act('retry', () => api.post('/admin/google-calendar/retry-sync').then(async (r) => {
                  toast.success(`Retried ${r.data.retried}, recovered ${r.data.recovered}`);
                  return api.get('/admin/google-calendar/status');
                }), 'Retry finished')}
                className="rounded-lg border-gray-200 text-xs shrink-0"
                data-testid="gc-retry-sync-btn"
              >
                {busy === 'retry' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Retry failed
              </Button>
            )}
          </div>
          {(s.sync.problems || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="gc-sync-problems">
              {s.sync.problems.map((p) => (
                <div key={p.session_id} className="text-xs bg-[#F8F9FA] rounded-lg px-3 py-2">
                  <span className="text-[#1F2937] font-medium">{p.career_name}</span>
                  <span className="text-[#9CA3AF]"> · {p.school_id} · {p.assigned_date} {p.assigned_time_slot}</span>
                  <span className={p.status === 'failed' ? ' text-red-600' : ' text-amber-600'}> · {p.status}</span>
                  {p.error && <p className="text-[#6B7280] mt-0.5">{p.error}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Delete test event?</DialogTitle>
            <DialogDescription>
              This removes the verification event from the {s.calendar_name || 'configured'} calendar. The Google connection stays active.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl border-gray-200" onClick={() => setConfirmDelete(false)} data-testid="gc-delete-cancel-btn">
              Cancel
            </Button>
            <Button className="bg-red-600 text-white hover:bg-red-700 rounded-xl" onClick={deleteTestEvent} data-testid="gc-delete-confirm-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
